//TODO remove this 
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react"
import Paper from "@mui/material/Paper"
import Typography from "@mui/material/Typography"
import LightTooltip from "../LightTooltip"
import { COLORS, KFC_BRANDS, isAmbientProduct } from "../../utils"
import makeStyles from "@mui/styles/makeStyles"
import clsx from "clsx"
import moment from "moment"
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined"
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined"
import { FRIDGE_FILTERS, isStockDeclared } from "../../utils/inventoryUtils"

/**
 * KFC-352: the two stock cells at the bottom of an inventory tile.
 *
 * Glyphs and colours are the ones DSG-36 specifies, and the same ones KFC-284 introduced on
 * the reception screen — so a Comptoir Manager reads the same visual for the same fridge
 * wherever they are in the tool.
 */
const STOCK_CELLS = [
  {
    id: "retail",
    fridgeFilter: FRIDGE_FILTERS.RETAIL,
    label: "libre-service",
    Icon: StorefrontOutlinedIcon
  },
  {
    id: "clickAndCollect",
    fridgeFilter: FRIDGE_FILTERS.CLICK_AND_COLLECT,
    label: "click & collect",
    Icon: ShoppingBagOutlinedIcon
  }
]

/**
 * KFC-352: colours read off the DSG-36 prototype, state by state.
 *
 * The point that is easy to get wrong: on the inventory tile the colour encodes the **state**,
 * not the fridge. Both cells turn green once the product has been inventoried, grey once it is
 * empty, white once the tile is selected. The fridge is told apart by its glyph alone — there
 * is no green-versus-amber pairing here, unlike the reception cards of KFC-284.
 *
 * The product code and the DLC keep the same greys whatever the state, except on a selected
 * tile. A green tile does not turn its code green.
 *
 * Applied inline rather than through classes: the shared `.done`, `.zero` and `.selected`
 * rules repaint every descendant with `& *`, and the reception screen still needs them.
 */
const TILE_COLORS = {
  DEFAULT: { tile: "#FFFFFF", border: "#E0E0E0", code: "#555555", dlc: "#8A8A8A", icon: "#202124", value: "#3C4043" },
  DONE: { tile: "#DFF2E4", border: "#E0E0E0", code: "#555555", dlc: "#8A8A8A", icon: "#2E7D45", value: "#2E7D45" },
  ZERO: { tile: "#ECECEC", border: "#ECECEC", code: "#555555", dlc: "#8A8A8A", icon: "#ADADAB", value: "#ADADAB" },
  SELECTED: { tile: "#2196F3", border: "#2196F3", code: "#FFFFFF", dlc: "rgba(255, 255, 255, 0.9)", icon: "#FFFFFF", value: "#FFFFFF" }
}

/**
 * KFC-352: the DLC band, measured on the prototype rather than reused from the app palette.
 *
 * It spans the whole tile and is centred — the shared `.date` rule caps it at 80px, which is
 * why the inventory renders its own element instead.
 */
const DLC_BAND = { background: "#F4A672", color: "#9A5115" }

/**
 * KFC-285: a stock cell painted on its own, when one fridge has been counted and the other has
 * not.
 *
 * `#F0F0EE` is the prototype's half-cell grey, distinct from the grey of a tile that is empty on
 * every fridge, so a half-counted tile never reads as a fully empty one. The prototype pairs it
 * with `#EFEFEE` for the full tile; this screen has shipped `#ECECEC` there since KFC-352, which
 * happens to widen the contrast rather than narrow it. The discrepancy is on the full tile, not
 * here, and changing a colour that has already been recetted is not this ticket's business.
 *
 * The glyph colours follow the state, as everywhere else on this tile. KFC-352 settled that the
 * fridge is told apart by its shape alone — the prototype offers a green-and-amber variant, and
 * the black-glyph one is what was chosen and recetted — so a cell that is not painted keeps the
 * tile's palette untouched.
 */
const CELL_COLORS = {
  none: null,
  green: { background: "#DFF2E4", icon: "#2E7D45", value: "#2E7D45" },
  grey: { background: "#F0F0EE", icon: "#B9B6AE", value: "#B9B6AE" }
}

/** Matches the tile's 12px radius, less its 1px border. */
const CELL_CORNER_RADIUS = 11

/** An undeclared stock, which is not the same thing as a stock of zero. */
const NO_STOCK = "—"

const useClasses = makeStyles(() => ({
  root: {
    color: COLORS.DARK_GREY,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    "&.date": {
      fontWeight: "normal",
      fontSize: 12,
      width: "80px",
      "&.dlcClose": {
        backgroundColor: COLORS.ORANGE_INVENTORY_BACKGROUND
      },
      "&.dlcPassedBg": {
        backgroundColor: COLORS.RED_INVENTORY_BACKGROUND
      },
    },
    "&.dlcPassed": {
      border: `2px solid ${COLORS.RED_INVENTORY_BACKGROUND}`,
    },
    "&.done": {
      backgroundColor: COLORS.GREEN_CARD_INVENTORY,
      border: "none",
      boxShadow: "none",
      "& *": {
        color: COLORS.GREEN_CARD_TEXT,
      },
    },
    "&.zero, &.zero.done": {
      backgroundColor: COLORS.GREY_CARD_BLURED,
      border: "none",
      boxShadow: "none",
      "& *": {
        color: COLORS.DARK_GREY,
      },
    },
    "&.selected, &.selected.done, &.selected.zero, &.selected.zero.done": {
      backgroundColor: COLORS.PRIMARY_COLOR,
      "& *": {
        color: COLORS.WHITE,
      },
    },
  },
  number: {
    fontWeight: "normal",
    fontSize: 24,
    "& span": {
      fontWeight: "bold",
      color: COLORS.BLACK_CARD,
    },
  },
  reusable: {
    mask: "url(/img/recycle.svg) no-repeat center",
    width: 10,
    height: 10,
    marginRight: 5,
    marginTop: 2,
  },
  // KFC-352 — the inventory tile: code and DLC on top, stock cells at the bottom.
  inventoryTop: {
    flex: "1 1 auto",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  // KFC-352 — measured on the DSG-36 prototype.
  inventoryCode: {
    fontSize: 19,
    lineHeight: "19px",
  },
  inventoryDate: {
    width: "100%",
    textAlign: "center",
    fontSize: 12,
    fontWeight: 400,
    lineHeight: "14px",
    marginTop: 4,
  },
  inventoryDateBand: {
    fontWeight: 600,
    padding: "2px 0",
    marginTop: 5,
  },
  stockRow: {
    width: "100%",
    display: "flex",
    alignItems: "stretch",
    borderTop: "1px solid #E0E0E0",
  },
  stockCell: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    padding: "6px 4px 7px",
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1.2,
    overflow: "hidden",
    "& + &": {
      borderLeft: "1px solid #E0E0E0",
    },
    "& svg": {
      fontSize: 14,
      flex: "0 0 auto",
    },
  },
}))

const Card = (props) => {
  const {
    type,
    number,
    date,
    onCardClick,
    selectedCard,
    element,
    done,
    isInventory,
    canEdit,
    fridgeFilter = FRIDGE_FILTERS.ALL,
    hasRetailOffer = false,
  } = props

  /**
   * KFC-352: the two-fridge tile belongs to hubs with an active retail offer and to them only —
   * "Ceci ne doit être visible que pour les centres de distribution qui ont une offre retail
   * active". Everywhere else the inventory keeps exactly the screen it had before this ticket,
   * so a hub with a single fridge is never shown a self-service column it does not have, its
   * value forever a dash.
   *
   * Defaulting to `false` is deliberate: a caller that forgets to pass it falls back to the
   * screen that already worked, rather than silently showing the new one to every hub.
   */
  const showRetailInventory = isInventory && Boolean(hasRetailOffer)

  const classes = useClasses()
  const selected = selectedCard && element.objectId === selectedCard.objectId
  const [dlcClose, setDlcClose] = useState()
  const [dlcPassed, setDlcPassed] = useState()

  const onClick = () => {
    if (canEdit && ((!done && !isInventory) || isInventory)) {
      onCardClick(element)
    }
  }

  useEffect(() => {
    const currentDate = moment.utc().startOf("day").valueOf()

    // KFC-352: on the inventory screen the DSG-36 prototype shows a single orange band as
    // soon as the DLC is reached — no red border, no separate "passed" state. Reception keeps
    // its two states: it was just reworked by KFC-284 and nothing asks to change it.
    if (showRetailInventory) {
      setDlcClose(element.dlc <= currentDate)
      setDlcPassed(false)

      return
    }

    // Both are set on every run, so a card that is reused for another product cannot keep
    // the previous one's warning.
    setDlcPassed(element.dlc < currentDate)
    setDlcClose(element.dlc === currentDate)
  }, [element, showRetailInventory])
  const dates = (element && element.events.filter(e => e.type === "INVENTORY").map(e => e.date && moment.utc(e.date).startOf("day").valueOf())) || []
  const isUpdated = dates.includes(moment.utc().startOf("day").valueOf())
  // KFC-352: in a single-fridge view, emptiness is judged on the fridge on screen — a product
  // out of click & collect but stocked in self-service is not empty in the self-service view.
  const isZero = FRIDGE_FILTERS.RETAIL === fridgeFilter
    ? Boolean(element.retailZero)
    : FRIDGE_FILTERS.CLICK_AND_COLLECT === fridgeFilter
      ? Boolean(element.clickAndCollectZero)
      : Boolean(element.zero)

  const reusableMark = element.isReusable && (
    <div
      className={classes.reusable}
      style={{
        backgroundColor: KFC_BRANDS.find((el) => el.name === element.brand)["color"],
      }}
    />
  )

  // KFC-285: on the inventory screen a product is "done" per fridge, not as a whole. In a
  // single-fridge view the tile turns green as soon as *that* fridge is counted; in "Tous" it
  // waits for every fridge that holds a stock — which is why counting the self-service side of
  // a product with no click & collect stock turns the whole tile green.
  const countedFor = FRIDGE_FILTERS.RETAIL === fridgeFilter
    ? Boolean(element.countedLibreService)
    : FRIDGE_FILTERS.CLICK_AND_COLLECT === fridgeFilter
      ? Boolean(element.countedClickAndCollect)
      : Boolean(element.allDeclaredCounted)

  // A count no longer colours the tile once it has expired; the per-fridge flags already carry
  // that delay, so the tile goes back to white and asks to be counted again.
  const inventoryDone = countedFor

  // The hover text follows the same view as the colour. A single-fridge view names the count
  // taken on that fridge and nothing else; a fridge never counted has no label, so no tooltip.
  // The "Tous" view keeps the product-level label, both fridges being on screen.
  const inventoryLabelForView = (FRIDGE_FILTERS.RETAIL === fridgeFilter
    ? element.lastInventoryLabelLibreService
    : FRIDGE_FILTERS.CLICK_AND_COLLECT === fridgeFilter
      ? element.lastInventoryLabelClickAndCollect
      : element.lastInventoryLabel) || ""

  const isDone = showRetailInventory ? inventoryDone : done

  // KFC-352: the prototype's palette, chosen state first — a selected tile wins over an empty
  // one, an empty one over an inventoried one.
  const tileColors = selected
    ? TILE_COLORS.SELECTED
    : isZero
      ? TILE_COLORS.ZERO
      : isDone
        ? TILE_COLORS.DONE
        : TILE_COLORS.DEFAULT

  /**
   * KFC-285: the state of one stock cell, when only part of the product has been counted.
   *
   * The ticket calls it "le coin en bas à gauche / à droite"; in the prototype it is the stock
   * cell itself that is painted, with its own rounded corner. It only ever happens in the
   * "Tous" view: a single-fridge view colours the whole tile instead, and a tile that is
   * selected, empty, or fully counted has a state of its own that wins.
   */
  const cellState = (cell) => {
    if (!showRetailInventory || selected || isZero || inventoryDone) return "none"
    if (FRIDGE_FILTERS.ALL !== fridgeFilter) return "none"

    // Grey does not expire — an empty fridge is empty whenever it was counted — and the
    // fallthrough below keeps it once the count itself has expired.
    const counted = "retail" === cell.id ? element.countedLibreService : element.countedClickAndCollect
    const volume = "retail" === cell.id ? element.volumeLibreService : element.volume

    if (counted) return volume > 0 ? "green" : "grey"

    // KFC-351: un transfert peut vider un frigo sans que personne l'ait compte, et le ticket
    // demande que le coin passe quand meme au gris — « Stock LS = 2, CC = 3, je transfere 2 :
    // le coin bas a gauche devient gris ». Le vert reste reserve au comptage : un frigo plein
    // et non compte n'a rien d'inventorie, alors qu'un frigo vide est vide, quelle qu'en soit
    // la cause. Un frigo jamais renseigne garde son tiret et reste blanc.
    return isStockDeclared(volume) && volume <= 0 ? "grey" : "none"
  }

  const codeAndDate = (
    <>
      <Typography className={classes.number} noWrap>
        {type}
        <span>{number}</span>
      </Typography>
      <div className={clsx(
        classes.root,
        { dlcPassedBg: dlcPassed },
        { dlcClose: dlcClose },
        { date },
      )}>{date}</div>
    </>
  )

  // KFC-352: the inventory renders its own code and date. The shared `.date` rule caps the
  // date at 80px, where the prototype spans the whole tile, and the band has colours of its
  // own that the state palette must not override.
  const inventoryCodeAndDate = (
    <>
      <Typography
        className={clsx(classes.number, classes.inventoryCode)}
        noWrap
        style={{ color: tileColors.code }}
      >
        {type}
        <span style={{ color: tileColors.code }}>{number}</span>
      </Typography>
      <div
        className={clsx(classes.inventoryDate, { [classes.inventoryDateBand]: dlcClose })}
        style={dlcClose
          ? { backgroundColor: DLC_BAND.background, color: DLC_BAND.color }
          : { color: tileColors.dlc }}
      >{date}</div>
    </>
  )

  const paperClassName = clsx(
    classes.root,
    { dlcPassed: dlcPassed },
    { zero: isZero },
    { done: isDone },
    { selected: selected },
  )

  // KFC-352: the inventory tile carries one stock cell per fridge in view, each with its own
  // tooltip, and moves the authorship tooltip to the top half. The reception tile, and the
  // inventory of a hub without a retail offer, are left exactly as they were.
  if (showRetailInventory) {
    return (
      <>
        {(!isZero || isUpdated || !isAmbientProduct(element)) &&
          <Paper
            className={paperClassName}
            onClick={onClick}
            style={{
              backgroundColor: tileColors.tile,
              border: `1px solid ${tileColors.border}`,
              borderRadius: 12,
              boxShadow: "none",
            }}
          >
            {/* Who counted, and when — for the fridge this view actually shows. */}
            <LightTooltip title={inventoryLabelForView}>
              <div className={classes.inventoryTop}>
                {reusableMark}
                {inventoryCodeAndDate}
              </div>
            </LightTooltip>
            <div className={classes.stockRow}>
              {STOCK_CELLS
                .filter(cell => FRIDGE_FILTERS.ALL === fridgeFilter || cell.fridgeFilter === fridgeFilter)
                .map(cell => {
                  const volume = "retail" === cell.id ? element.volumeLibreService : element.volume
                  const declared = isStockDeclared(volume)
                  const state = cellState(cell)
                  const partialColors = CELL_COLORS[state]

                  return (
                    <LightTooltip
                      key={cell.id}
                      title={`Stock théorique ${cell.label} : ${declared ? volume : "non déclaré"}`}
                    >
                      <div
                        className={classes.stockCell}
                        // An undeclared stock takes the glyph's colour rather than the value's:
                        // the dash is a placeholder, not a counted quantity.
                        style={{
                          color: partialColors
                            ? partialColors.value
                            : (declared ? tileColors.value : tileColors.icon),
                          ...(partialColors
                            ? {
                              backgroundColor: partialColors.background,
                              // The cell hugs the tile's own corner, so only the outer one is
                              // rounded — the inner edge stays square against its neighbour.
                              [`borderBottom${"retail" === cell.id ? "Left" : "Right"}Radius`]: CELL_CORNER_RADIUS
                            }
                            : {})
                        }}
                      >
                        <cell.Icon
                          aria-hidden
                          style={{ color: partialColors ? partialColors.icon : tileColors.icon }}
                        />
                        {declared ? volume : NO_STOCK}
                      </div>
                    </LightTooltip>
                  )
                })}
            </div>
          </Paper>
        }
      </>
    )
  }

  return (
    <>
      {(!isZero || isUpdated || !isAmbientProduct(element)) &&
        <LightTooltip title={element.toolTip}>
          <Paper className={paperClassName} onClick={onClick}>
            {reusableMark}
            {codeAndDate}
          </Paper>
        </LightTooltip>
      }
    </>
  )
}

export default Card
